/**
 * «Can this browser draw a live map right now?» — asked once, answered for the
 * whole session.
 *
 * The SVG sketch (`MapSketch.tsx`) is NOT a placeholder. It is the other
 * renderer: with no key, in an old browser, offline, or after the WebGL context
 * is lost it draws the same walk from the same coordinates, and it always
 * works. The live map is the upgrade, and this module is the one place that
 * decides whether the upgrade is available.
 *
 * Four conditions, all of them required (design §1.3):
 *
 *  1. `VITE_DGIS_MAPGL_KEY` is non-empty — CI bakes it in, and an empty value
 *     is a WORKING configuration, not a failure;
 *  2. the loader script resolved (`@2gis/mapgl`'s `load()`, which appends a
 *     `<script>` to `mapgl.2gis.com` — hence the `script-src` in `CSP_PROD`);
 *  3. `mapgl.isSupported()` says the browser has what the SDK needs;
 *  4. nothing has called `markLiveMapBroken()` since.
 *
 * The switch to `sketch` is ONE-WAY. A map that lost its WebGL context or
 * failed to load its style flips over and stays over: flickering between two
 * renderers is worse than committing to the one that cannot fail.
 */

import { useEffect, useSyncExternalStore } from 'react';
import type * as MapGL from '@2gis/mapgl/types';

export type MapCapability = 'live' | 'sketch' | 'pending';

/**
 * The browser key. Empty when the bundle was built without one — which is what
 * every developer build and every branch pipeline does.
 *
 * It is a SEPARATE variable from the backend's `DGIS_API_KEY` on purpose: this
 * one is readable in DevTools by anyone who opens the stand, so rotating it
 * must not force a rotation of the key that pays for Routing (design §7.1).
 */
export function mapKey(): string {
  return import.meta.env.VITE_DGIS_MAPGL_KEY ?? '';
}

/** Bounds the blank frame when `mapgl.2gis.com` is reachable but not answering. */
const LOAD_TIMEOUT_MS = 12_000;

let phase: MapCapability = mapKey() ? 'pending' : 'sketch';
const listeners = new Set<() => void>();

function publish(next: MapCapability): void {
  if (phase === next) return;
  phase = next;
  for (const listener of listeners) listener();
}

/**
 * Gives up on the live map for the rest of the session.
 *
 * Called by the map host on `webglcontextlost` and `styleloaderror`, and by the
 * loader when the SDK never arrives.
 */
export function markLiveMapBroken(reason: string): void {
  if (phase === 'sketch') return;
  // eslint-disable-next-line no-console
  console.warn('[map] falling back to the sketch renderer:', reason);
  publish('sketch');
}

let sdk: Promise<typeof MapGL | null> | null = null;

/**
 * Loads the SDK once per process and reports the outcome through `phase`.
 *
 * MEMOISED DELIBERATELY. React 18's StrictMode mounts every effect twice in
 * development, and a loader that ran per mount would append a second script
 * tag — and, downstream, build a second map that quietly bills a second set of
 * tiles from a detached node (design §1.4, §10).
 */
export function ensureSdk(): Promise<typeof MapGL | null> {
  if (sdk) return sdk;
  const key = mapKey();
  if (!key) {
    publish('sketch');
    sdk = Promise.resolve(null);
    return sdk;
  }

  sdk = (async () => {
    try {
      const loader = await import('@2gis/mapgl');
      const loaded = await withTimeout(loader.load(), LOAD_TIMEOUT_MS);
      if (!loaded.isSupported()) {
        markLiveMapBroken(loaded.notSupportedReason() ?? 'isSupported() === false');
        return null;
      }
      publish('live');
      return loaded;
    } catch (error) {
      markLiveMapBroken(error instanceof Error ? error.message : String(error));
      return null;
    }
  })();
  return sdk;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`mapgl load timed out after ${ms} ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): MapCapability {
  return phase;
}

/**
 * The same question outside React.
 *
 * The decision is module state rather than component state, so it is readable
 * without a hook — which is what lets the tests assert it in a suite that has
 * no DOM (`vite.config.ts` runs vitest in `environment: 'node'`).
 */
export function mapCapabilityNow(): MapCapability {
  return phase;
}

/**
 * The renderer's question. Starts the load lazily — nothing is fetched until a
 * map card is actually on the board.
 */
export function useMapCapability(): MapCapability {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (value === 'pending') void ensureSdk();
  }, [value]);
  return value;
}

/** Tests only: the module-level decision is a session, and a suite is many. */
export function resetMapCapabilityForTests(): void {
  sdk = null;
  phase = mapKey() ? 'pending' : 'sketch';
  listeners.clear();
}
